import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import SiteLocationMap from '@/components/maps/SiteLocationMap';
import { useCreateCustomer } from './hooks/useCreateCustomer';
import { GeocodingResult } from '@/components/maps/mapbox';
import { AddressSearch } from '@/components/maps/AddressSearch';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';
import type { CustomerCreateDto } from './customerAPI';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

interface AddCustomerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: Partial<CustomerFormValues>;
}

// Form schema
const customerFormSchema = z.object({
  customerName: z.string().min(1, 'Customer name is required'),
  accountNumber: z.string().optional(),
  fullName: z.string().trim().min(1, 'Full name is required'),
  address: z.object({
    addressLine1: z.string().min(1, 'Address is required'),
    addressLine2: z.string().optional(),
    city: z.string(),
    postCode: z.string(),
    region: z.string(),
    country: z.string(),
    lat: z.number(),
    lon: z.number(),
    timezone: z.string().optional(),
  }),
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

export function AddCustomer({ open, onOpenChange, initialValues }: AddCustomerProps) {
  const [mapLat, setMapLat] = useState(-36.8509);
  const [mapLon, setMapLon] = useState(174.7645);

  const defaultAddress = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    postCode: '',
    region: '',
    country: '',
    lat: mapLat,
    lon: mapLon,
    timezone: '',
  };

  const defaultFormValues: CustomerFormValues = {
    customerName: '',
    accountNumber: '',
    fullName: '',
    address: defaultAddress,
  };

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      ...defaultFormValues,
      ...initialValues,
      address: {
        ...defaultAddress,
        ...initialValues?.address,
      },
    },
  });

  const createCustomerMutation = useCreateCustomer();
  const navigate = useNavigate();

  const onSubmit = async (data: CustomerFormValues) => {
    try {
      const payload: CustomerCreateDto = {
        name: data.customerName.trim(),
        address: data.address,
      };

      const accountNumber = data.accountNumber?.trim();
      if (accountNumber) {
        payload.accountNumber = accountNumber;
      }

      const fullName = data.fullName?.trim();
      if (fullName) {
        payload.fullName = fullName;
      }

      const result = await createCustomerMutation.mutateAsync(payload);
      form.reset();
      onOpenChange(false);
      const newId = result?.customerId;
      if (newId) {
        navigate(`/customers/${newId}`);
      } else {
        navigate('/customers');
      }
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[900px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            data-testid="add-customer-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-2 p-4"
          >
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <FormField
                  control={form.control}
                  name="customerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter customer name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-1">
                <FormField
                  control={form.control}
                  name="accountNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Number (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Account number (optional)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-2">
              <Label>Address & Location</Label>
              <div className="grid grid-cols-2 gap-4">
                {/* Left side - Address inputs and details */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="address.addressLine1"
                    render={() => (
                      <FormItem>
                        <FormControl>
                          <AddressSearch
                            onSelect={(result: GeocodingResult) => {
                              const newAddress = {
                                addressLine1: result.address || '',
                                addressLine2: form.getValues('address.addressLine2') || '',
                                city: result.city || '',
                                region: result.state || '',
                                country: result.country || '',
                                postCode: result.postcode || '',
                                lat: result.center[1],
                                lon: result.center[0],
                                timezone: result.timezone || '',
                              };
                              form.setValue('address', newAddress, { shouldValidate: true });
                              setMapLat(result.center[1]);
                              setMapLon(result.center[0]);
                            }}
                            placeholder="Search for an address"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="address.addressLine2"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input placeholder="address line 2 (optional)" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {
                    <div className="rounded-lg border border-gray-700 bg-[#111] p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs font-medium text-gray-400">Address Line 1</div>
                          <div className="text-sm text-white">
                            {form.watch('address.addressLine1') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Timezone</div>
                          <div className="text-sm text-white">
                            {form.watch('address.timezone') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">City</div>
                          <div className="text-sm text-white">
                            {form.watch('address.city') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Region</div>
                          <div className="text-sm text-white">
                            {form.watch('address.region') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Post Code</div>
                          <div className="text-sm text-white">
                            {form.watch('address.postCode') || '-'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-400">Country</div>
                          <div className="text-sm text-white">
                            {form.watch('address.country') || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                </div>

                {/* Right side - Map */}
                <div className="h-[300px]">
                  <SiteLocationMap
                    coordinates={{ lat: mapLat, lng: mapLon }}
                    onCoordinatesChange={(coords) => {
                      setMapLat(coords.lat);
                      setMapLon(coords.lng);
                      const newAddress = {
                        ...form.getValues('address'),
                        lat: coords.lat,
                        lon: coords.lng,
                      };
                      form.setValue('address', newAddress, { shouldValidate: true });
                    }}
                    onAddressChange={() => {}}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="mt-2">
              <Button type="submit" disabled={createCustomerMutation.isPending}>
                {createCustomerMutation.isPending ? 'Creating...' : 'Create Customer'}
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
